import Vue from 'vue'
import {
  list,
  save,
  realty,
  details,
  update,
  remove,
  addPhotos,
  addVideos,
  quickUpdate,
  deletePhotos,
  deleteVideos,
  getRealtyPhotos,
  getRealtyVideos,
  vistaOptions,
} from '@/services/realty/realtyService'

// campos exigidos pela Vista no cadastro, vindos de GET /web/realty/vistaOptions
const emptyVistaRealtyOptions = () => ({
  push_enabled: false,
  categoria: [],
  finalidade: [],
  situacao: [],
  ocupacao: [],
  status: [],
})

const state = {
  realty: {},
  realties: [],
  realtyPhotos: [],
  realtyVideos: [],
  updateRealtyDialog: false,
  createRealtySuccess: false,
  createdRealtyId: null,
  addRealtyPhotosSuccess: false,
  addRealtyVideosSuccess: false,
  isRealtyListLoading: false,
  isRealtyLoading: false,
  vistaRealtyOptions: emptyVistaRealtyOptions(),
}

const getters = {
  realty: (state) => state.realty,
  realties: (state) => state.realties,
  realtyPhotos: (state) => state.realtyPhotos,
  realtyVideos: (state) => state.realtyVideos,
  updateRealtyDialog: (state) => state.updateRealtyDialog,
  createRealtySuccess: (state) => state.createRealtySuccess,
  createdRealtyId: (state) => state.createdRealtyId,
  addRealtyPhotosSuccess: (state) => state.addRealtyPhotosSuccess,
  addRealtyVideosSuccess: (state) => state.addRealtyVideosSuccess,
  isRealtyListLoading: (state) => state.isRealtyListLoading,
  isRealtyLoading: (state) => state.isRealtyLoading,
  vistaRealtyOptions: (state) => state.vistaRealtyOptions,
}

const actions = {
  async fetchRealtyDetails({ commit, dispatch, rootState }, realtyId) {
    const { date, type, real_estate_id } = rootState.appointmentForm.appointmentFields
    const body = { date, realty_id: realtyId, service_type: type, real_estate_id }

    commit('setIsRealtyListLoading', true)

    try {
      const { data } = await details(body)
      commit('setRealty', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsRealtyListLoading', false)
    }
  },

  async fetchRealties({ commit, dispatch, rootState }) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = { ...defaultParams, ...rootState.filters.filters }

    try {
      const { data } = await list(params)
      const { result, total } = await data
      commit('setRealties', result)
      commit('setTotalPages', total)
    } catch (error) {
      dispatch('showErrorMessage', error)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchRealty({ commit, dispatch }, realtyId) {
    commit('setIsRealtyLoading', true)

    try {
      const { data } = await realty(realtyId)
      commit('setRealty', data)
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsRealtyLoading', false)
    }
  },

  async createRealty({ commit, dispatch }, realty) {
    commit('setIsLoading', true)
    commit('setCreateRealtySuccess', false)

    await save(realty)
      .then((response) => {
        dispatch('showSuccessMessage', Vue.i18n.translate('realty.success.realtyCreated'))
        commit('setCreatedRealtyId', response.data.id)
        commit('setCreateRealtySuccess', true)
      })
      .catch((error) => {
        const data = error?.data

        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async updateRealty({ commit, dispatch }, body) {
    commit('setIsLoading', true)
    commit('setCreateRealtySuccess', false)

    await update(body)
      .then((response) => {
        dispatch('showSuccessMessage', Vue.i18n.translate('realty.success.realtyUpdated'))
        commit('setCreatedRealtyId', response.data.id)
        commit('setCreateRealtySuccess', true)
      })
      .catch(({ data }) => {
        setTimeout(() => {
          data?.errors && data?.errors?.length >= 1
            ? dispatch('showErrorMessage', data.errors[0].message)
            : dispatch('showErrorMessage', data.message)
        }, 500)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async deleteRealty({ dispatch }, realtyId) {
    await remove(realtyId)
      .then(() => dispatch('showSuccessMessage', Vue.i18n.translate('realty.success.realtyRemoved')))
      .catch(({ data }) => dispatch('showErrorMessage', data.message))
  },


  async addRealtyPhotos({ commit, dispatch }, body) {
    commit('setIsLoading', true)
    commit('setAddRealtyPhotosSuccess', false)

    await addPhotos(body)
      .then(({ status }) => {
        commit('setAddRealtyPhotosSuccess', true)
        dispatch('showSuccessMessage', 'Foto(s) adicionada(s) com sucesso!')
      })
      .catch(({ data }) => {
        setTimeout(() => {
          data?.errors && data?.errors?.length >= 1
            ? dispatch('showErrorMessage', data.errors[0].message)
            : dispatch('showErrorMessage', data.message)
        }, 500)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async deleteRealtyPhotos({ commit, dispatch }, body) {
    commit('setIsLoading', true)

    await deletePhotos(body)
      .then(() => {
        dispatch('showSuccessMessage', 'Foto(s) removida(s) com sucesso!')
      })
      .catch(({ data }) => {
        setTimeout(() => {
          data?.errors && data?.errors?.length >= 1
            ? dispatch('showErrorMessage', data.errors[0].message)
            : dispatch('showErrorMessage', data.message)
        }, 500)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async addRealtyVideos({ commit, dispatch }, body) {
    commit('setIsLoading', true)
    commit('setAddRealtyVideosSuccess', false)

    await addVideos(body)
      .then(() => {
        commit('setAddRealtyVideosSuccess', true)
        dispatch('showSuccessMessage', 'Vídeo(s) adicionado(s) com sucesso!')
      })
      .catch(({ data }) => {
        setTimeout(() => {
          data?.errors && data?.errors?.length >= 1
            ? dispatch('showErrorMessage', data.errors[0].message)
            : dispatch('showErrorMessage', data.message)
        }, 500)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async deleteRealtyVideos({ commit, dispatch }, body) {
    commit('setIsLoading', true)

    await deleteVideos(body)
      .then(() => {
        dispatch('showSuccessMessage', 'Vídeo(s) removido(s) com sucesso!')
      })
      .catch(({ data }) => {
        setTimeout(() => {
          data?.errors && data?.errors?.length >= 1
            ? dispatch('showErrorMessage', data.errors[0].message)
            : dispatch('showErrorMessage', data.message)
        }, 500)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async fetchRealtyVideos({ dispatch, commit }, realtyId) {
    commit('setIsLoading', true)

    await getRealtyVideos(realtyId)
      .then(({ data }) => {
        commit('setRealtyVideos', data)
      })
      .catch(({ data }) => {
        setTimeout(() => {
          data?.errors && data?.errors?.length >= 1
            ? dispatch('showErrorMessage', data.errors[0].message)
            : dispatch('showErrorMessage', data.message)
        }, 500)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async quickRealtyUpdate({dispatch}, realtyDetails) {
    await quickUpdate(realtyDetails)
      .then(({ status }) => {
        status === 200 &&
          dispatch(
            'showSuccessMessage',
            Vue.i18n.translate('realty.success.realtyDetailsUpdated')
          )
      }).catch(({data}) => {
        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
      })
  },

  async fetchRealtyPhotos({ dispatch, commit }, realtyId) {
    commit('setIsLoading', true)

    await getRealtyPhotos(realtyId)
      .then(({ data }) => {
        commit('setRealtyPhotos', data)
      })
      .catch(({ data }) => {
        setTimeout(() => {
          data?.errors && data?.errors?.length >= 1
            ? dispatch('showErrorMessage', data.errors[0].message)
            : dispatch('showErrorMessage', data.message)
        }, 500)
      })
      .finally(() => {
        commit('setIsLoading', false)
      })
  },

  async fetchVistaRealtyOptions({ commit, dispatch }, realEstateId) {
    try {
      const { data } = await vistaOptions({ realEstateId })
      commit('setVistaRealtyOptions', data)
    } catch ({ data }) {
      commit('setVistaRealtyOptions', null)
      dispatch('showErrorMessage', data?.message)
    }
  },

  closeUpdateRealtyDialog({ commit }) {
    commit('setUpdateRealtyDialogStatus', false)
  },

  openUpdateRealtyDialog({ commit }) {
    commit('setUpdateRealtyDialogStatus', true)
  },
}

const mutations = {
  setRealty: (state, realty) => {
    state.realty = realty
  },

  setRealties: (state, realties) => {
    state.realties = realties
  },

  setUpdateRealtyDialogStatus: (state, status) => {
    state.updateRealtyDialog = status
  },

  setRealtyPhotos: (state, photos) => {
    state.realtyPhotos = photos
  },

  setCreateRealtySuccess: (state, success) => {
    state.createRealtySuccess = success
  },

  setAddRealtyPhotosSuccess: (state, success) => {
    state.addRealtyPhotosSuccess = success
  },

  setAddRealtyVideosSuccess: (state, success) => {
    state.addRealtyVideosSuccess = success
  },

  setRealtyVideos: (state, videos) => {
    state.realtyVideos = videos || []
  },

  setCreatedRealtyId: (state, id) => {
    state.createdRealtyId = id
  },

  setIsRealtyListLoading: (state, status) => {
    state.isRealtyListLoading = status
  },

  setIsRealtyLoading: (state, status) => {
    state.isRealtyLoading = status
  },

  setVistaRealtyOptions: (state, options) => {
    state.vistaRealtyOptions = options ?? emptyVistaRealtyOptions()
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
}
